import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  fetchPresets,
  type CleanupPolicy,
  type PackshotPresets,
} from '@/lib/packshots';
import { fetchSession, hasPermission } from '@/lib/session';

type ShotDefaults = {
  cleanupPolicy: CleanupPolicy;
  cleanupPreset: string;
  cutoutPreset: string;
  presets: PackshotPresets | null;
  setCleanupPolicy: (value: CleanupPolicy) => void;
  setCleanupPreset: (value: string) => void;
  setCutoutPreset: (value: string) => void;
};

const ShotDefaultsContext = createContext<ShotDefaults | null>(null);

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function ShotDefaultsProvider({ children }: { children: ReactNode }) {
  const [canWrite, setCanWrite] = useState(false);
  const [presets, setPresets] = useState<PackshotPresets | null>(null);
  const [cleanupPolicy, setCleanupPolicy] = useState<CleanupPolicy>('auto');
  const [cleanupPreset, setCleanupPreset] = useState('');
  const [cutoutPreset, setCutoutPreset] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const session = await fetchSession();
      if (!cancelled) setCanWrite(hasPermission(session, 'packshots.write'));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canWrite) return;
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchPresets();
        if (cancelled) return;
        setPresets(next);
        setCleanupPolicy(next.defaults.cleanupPolicy);
        const cleanupDefault = next.defaults.cleanupPreset && next.cleanup.some((item) => item.key === next.defaults.cleanupPreset)
          ? next.defaults.cleanupPreset
          : next.cleanup[0]?.key ?? '';
        const cutoutDefault = next.defaults.cutoutPreset && next.cutout.some((item) => item.key === next.defaults.cutoutPreset)
          ? next.defaults.cutoutPreset
          : next.cutout[0]?.key ?? '';
        setCleanupPreset(cleanupDefault);
        setCutoutPreset(cutoutDefault);
      } catch (err) {
        if (!cancelled) toast.error(errorText(err, 'Could not load models'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canWrite]);

  return (
    <ShotDefaultsContext.Provider value={{
      cleanupPolicy,
      cleanupPreset,
      cutoutPreset,
      presets,
      setCleanupPolicy,
      setCleanupPreset,
      setCutoutPreset,
    }}>
      {children}
    </ShotDefaultsContext.Provider>
  );
}

export function useShotDefaults(): ShotDefaults {
  const value = useContext(ShotDefaultsContext);
  if (!value) throw new Error('Shot defaults are missing');
  return value;
}

export default function ModelsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const defaults = useShotDefaults();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Models</DialogTitle>
          <DialogDescription>These apply to the next shots you create.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <PolicyField value={defaults.cleanupPolicy} onChange={defaults.setCleanupPolicy} />
          <PresetField
            label="Cleanup model"
            value={defaults.cleanupPreset}
            options={defaults.presets?.cleanup ?? []}
            onChange={defaults.setCleanupPreset}
          />
          <PresetField
            label="Cutout model"
            value={defaults.cutoutPreset}
            options={defaults.presets?.cutout ?? []}
            onChange={defaults.setCutoutPreset}
          />
        </div>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PolicyField({ value, onChange }: { value: CleanupPolicy; onChange: (value: CleanupPolicy) => void }) {
  return (
    <div className="space-y-1.5">
      <Label>Cleanup</Label>
      <Select value={value} onValueChange={(next) => {
        if (next === 'auto' || next === 'always' || next === 'never') onChange(next);
      }}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="auto">Auto</SelectItem>
          <SelectItem value="always">Always</SelectItem>
          <SelectItem value="never">Never</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function PresetField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: PackshotPresets['cleanup'];
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value || undefined} onValueChange={onChange} disabled={options.length === 0}>
        <SelectTrigger>
          <SelectValue placeholder={options.length === 0 ? 'Loading…' : 'Choose'} />
        </SelectTrigger>
        <SelectContent>
          {options.map((preset) => (
            <SelectItem key={preset.key} value={preset.key}>{preset.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
